// ═══════════════════════════════════════════════════════════════════
//  modules/sanctions/SanctionsStats.js
//  L'indicateur qui compte vraiment est la part de sanctions hors
//  barème : c'est lui qui dit si le guide est appliqué ou contourné.
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { THEME } from "../../socle/theme";
import { NIVEAU_STYLE } from "./constantes";

function Tuile({ label, valeur, sous, couleur }) {
  return (
    <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: "13px 15px" }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: couleur || THEME.text, lineHeight: 1.1 }}>{valeur}</div>
      <div style={{ color: THEME.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 3 }}>{label}</div>
      {sous && <div style={{ color: THEME.textDim, fontSize: 11.5, marginTop: 3 }}>{sous}</div>}
    </div>
  );
}

function Barres({ titre, lignes, couleur }) {
  const max = Math.max(1, ...lignes.map((l) => l.n));
  return (
    <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 16 }}>
      <div style={{ color: THEME.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>{titre}</div>
      {lignes.length === 0 && <div style={{ color: THEME.textDim, fontSize: 13 }}>Aucune donnée.</div>}
      {lignes.map((l) => (
        <div key={l.cle} style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: THEME.text, marginBottom: 3 }}>
            <span>{l.label}</span><span style={{ color: THEME.textMuted }}>{l.n}{l.sous ? ` · ${l.sous}` : ""}</span>
          </div>
          <div style={{ background: THEME.surface, borderRadius: 4, height: 6, overflow: "hidden" }}>
            <div style={{ width: `${(l.n / max) * 100}%`, height: "100%", background: couleur || THEME.accent }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SanctionsStats({ api, bareme }) {
  const [s, setS] = useState(null);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    try { setS(await api.stats()); setErreur(null); } catch (e) { setErreur(e.message); }
  }, [api]);
  useEffect(() => { charger(); }, [charger]);

  if (erreur) return <div style={{ color: THEME.red }}>{erreur}</div>;
  if (!s) return <div style={{ color: THEME.textMuted }}>Chargement…</div>;

  const total = Number(s.global.total) || 0;
  const hb = Number(s.global.hors_bareme) || 0;
  const partHB = total ? Math.round((hb / total) * 100) : 0;
  const levees = Number(s.global.levees) || 0;
  const partLevees = total ? Math.round((levees / total) * 100) : 0;

  const libelleMotif = (motif, niveau) =>
    ((bareme && bareme.bareme[niveau]) || {})[motif] ? bareme.bareme[niveau][motif].label : motif;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <Tuile label="Sanctions au total" valeur={total} />
        <Tuile label="En cours" valeur={Number(s.global.actives) || 0} couleur={THEME.red} />
        <Tuile label="Hors barème" valeur={`${partHB} %`} sous={`${hb} sanction(s)`}
          couleur={partHB > 25 ? THEME.amber : THEME.emerald} />
        <Tuile label="Levées" valeur={`${partLevees} %`} sous={`${levees} sanction(s)`} couleur={THEME.textMuted} />
        <Tuile label="Contestées" valeur={Number(s.global.contestees) || 0} couleur={THEME.accent} />
      </div>

      {partHB > 25 && total >= 10 && (
        <div style={{ background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 10, padding: "11px 14px", color: THEME.amber, fontSize: 13 }}>
          Plus d'un quart des sanctions s'écartent du barème. Soit les durées du guide ne
          correspondent pas à la réalité du terrain, soit l'échelle n'est pas appliquée — dans les
          deux cas ça se règle en équipe, pas dans le code.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
        <Barres titre="Par niveau (12 mois)" couleur={THEME.red}
          lignes={s.par_niveau.map((r) => ({ cle: r.niveau, label: (NIVEAU_STYLE[r.niveau] || {}).label || r.niveau, n: Number(r.n) }))} />
        <Barres titre="Motifs les plus fréquents" couleur={THEME.amber}
          lignes={s.par_motif.map((r) => ({ cle: `${r.niveau}:${r.motif}`, label: libelleMotif(r.motif, r.niveau), n: Number(r.n) }))} />
      </div>

      <Barres titre="Activité par modérateur — charge de travail, pas performance" couleur={THEME.accent}
        lignes={s.par_moderateur.map((r) => ({
          cle: r.pseudo || "?", label: r.pseudo || "—", n: Number(r.n),
          sous: `${Number(r.hors_bareme) || 0} hors barème`,
        }))} />

      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 16 }}>
        <div style={{ color: THEME.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
          Dossiers double compte
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, fontSize: 13 }}>
          <div><b style={{ color: THEME.text }}>{Number(s.dossiers.total) || 0}</b> <span style={{ color: THEME.textMuted }}>au total</span></div>
          <div><b style={{ color: THEME.accent }}>{Number(s.dossiers.observation) || 0}</b> <span style={{ color: THEME.textMuted }}>en observation</span></div>
          <div><b style={{ color: THEME.emerald }}>{Number(s.dossiers.liste_blanche) || 0}</b> <span style={{ color: THEME.textMuted }}>autorisés</span></div>
          <div><b style={{ color: THEME.amber }}>{Number(s.dossiers.ban_propose) || 0}</b> <span style={{ color: THEME.textMuted }}>bans proposés</span></div>
          <div><b style={{ color: THEME.red }}>{Number(s.dossiers.ban_applique) || 0}</b> <span style={{ color: THEME.textMuted }}>bans appliqués</span></div>
        </div>
        <div style={{ color: THEME.textDim, fontSize: 12, marginTop: 10 }}>
          Une majorité de dossiers classés en liste blanche est normale : c'est le signe que la
          détection attrape surtout des familles, et que le garde-fou fonctionne.
        </div>
      </div>
    </div>
  );
}
